import React from 'react';
import { Avatar, Box, Tooltip, Typography, alpha, useTheme } from '@mui/material';

import type { AgentLabel } from '../../utils/Agent/labels';

type CompoundInlineLabelProps = {
    label: AgentLabel;
    size?: 'default' | 'compact';
};

export default function CompoundInlineLabel({ label, size = 'default' }: CompoundInlineLabelProps) {
    const theme = useTheme();
    const accentColor = theme.palette.mode === 'light' ? theme.palette.secondary.main : theme.palette.primary.main;
    const isCompact = size === 'compact';

    return (
        <Tooltip title={label.labelName}>
            <Box
                component="span"
                sx={{
                    flex: '0 0 auto',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: isCompact ? 0.45 : 0.6,
                    px: isCompact ? 0.65 : 0.8,
                    py: isCompact ? 0.25 : 0.35,
                    borderRadius: 999,
                    border: `1px solid ${alpha(accentColor, theme.palette.mode === 'light' ? 0.22 : 0.34)}`,
                    background: theme.palette.mode === 'light'
                        ? `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.92)} 0%, ${alpha(accentColor, 0.12)} 100%)`
                        : `linear-gradient(180deg, ${alpha(accentColor, 0.28)} 0%, ${alpha(theme.palette.background.paper, 0.92)} 100%)`,
                    boxShadow: theme.palette.mode === 'light'
                        ? `0 4px 12px ${alpha(accentColor, 0.12)}, inset 0 1px 0 ${alpha(theme.palette.common.white, 0.7)}`
                        : `0 6px 14px ${alpha(theme.palette.common.black, 0.24)}, inset 0 1px 0 ${alpha(theme.palette.common.white, 0.08)}`,
                    maxWidth: '100%',
                    verticalAlign: 'middle',
                    fontSize: isCompact ? 'min(0.72em, 0.82rem)' : 'min(0.82em, 0.95rem)',
                    lineHeight: 1,
                    backdropFilter: 'blur(6px)',
                }}
            >
                {label.logo && (
                    <Avatar
                        src={`data:image/*;base64,${label.logo}`}
                        alt={label.labelName}
                        variant="rounded"
                        sx={{
                            width: isCompact ? '1em' : '1.15em',
                            height: isCompact ? '1em' : '1.15em',
                            borderRadius: '0.3em',
                            boxShadow: `0 0 0 1px ${alpha(theme.palette.common.white, theme.palette.mode === 'light' ? 0.85 : 0.12)}`,
                        }}
                    />
                )}
                <Typography
                    component="span"
                    sx={{
                        fontWeight: 700,
                        fontSize: '1em',
                        lineHeight: 1,
                        letterSpacing: isCompact ? '0.005em' : '0.01em',
                        color: theme.palette.mode === 'light' ? alpha(theme.palette.text.primary, 0.82) : alpha(theme.palette.common.white, 0.88),
                    }}
                    noWrap
                >
                    {label.labelName}
                </Typography>
            </Box>
        </Tooltip>
    );
}
